import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { uuidv7 as uuid } from "uuidv7";
import axios from "axios";
import { IntlShape, WrappedComponentProps, injectIntl } from "react-intl";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { showToast } from "src/app/methods/showToast";
import { translateMessage } from "src/app/methods/translateMessage";
import { SocialProfile } from "src/app/modals/AddSocialProfilesModal/AddSocialProfilesModal";
import responseCodes from "src/utils/responseCodes";
import { RootState } from "src/redux/reducers";
import { WorkspacePermissionsEnum } from "src/utils/PermissionsEnums";
import { handle402Errors } from "src/redux/main/functions";
import ProjectsSearchSelector from "src/app/dropdowns/ProjectsSearchSelector/ProjectsSearchSelector";
import Modal, {
  ModalRow,
  ModalText,
  ModalTitle,
  OutsideClickModes,
} from "../../components/Modal/Modal";
import { ImageOption } from "../../components/CustomSelect/CustomSelect";
import { API_URLS } from "../../../utils/API_URLS";
import { ReactComponent as PlusIcon } from "../../../images/plus-alt.svg";
import { ReactComponent as RocketIcon } from "../../../images/rocket.svg";
import CustomCheckbox from "../../components/CustomCheckbox/CustomCheckbox";
import { MetaFieldType } from "../../methods/getMetaFieldTypeOptions";
import { isFacebookNumericUsername, SocialProvider } from "../../../types";
import { SelectOption } from "../CreateNewProjectPane/types";

import "./AddCreatorsCDTWorkspaceProjectModal.scss";

export interface SelectedCreator {
  name: string;
  uuid?: string;
  socialProfiles?: SocialProfile[];
  rank?: string | null;
  displayName?: string | null;
}

interface Props extends WrappedComponentProps<"intl"> {
  onClose: () => void;
  openCreateNewProjectModal: () => void;
  selectedCreators: SelectedCreator[];
  activeProjectUuid?: string;
  intl: IntlShape;
}

const AddCreatorsCDTWorkspaceProjectModal: React.FC<Props> = (props) => {
  const {
    onClose,
    activeProjectUuid,
    selectedCreators,
    openCreateNewProjectModal,
    intl,
  } = props;

  const [selectedWsProject, setSelectedWsProject] =
    useState<SelectOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [copyFieldValues, setCopyFieldValues] = useState(false);

  const dispatch = useDispatch();

  const {
    mainReducer: { activeWorkspaceUuid, identity },
  } = useSelector((state: RootState) => state);

  const copyTaskToWsProject = () => {
    if (selectedWsProject && selectedWsProject.value) {
      const tasksData = selectedCreators.map((creator) => {
        const isFacebookNumericName =
          creator.socialProfiles &&
          creator.socialProfiles[0].socialProvider ===
            SocialProvider.Facebook &&
          isFacebookNumericUsername(creator.name);

        if (creator.socialProfiles) {
          return {
            uuid: uuid(),
            name: creator.name,
            socialProfiles: creator.socialProfiles,
            rank: null,
            displayName: isFacebookNumericName ? creator?.displayName : null,
          };
        }

        return {
          uuid: uuid(),
          name: creator,
          rank: null,
          displayName: isFacebookNumericName ? creator?.displayName : null,
        };
      });

      const payload = {
        wsTaskWorkspaceUuid: activeWorkspaceUuid,
        projectId: selectedWsProject.value,
        taskType: "creator",
        tasksData,
      };

      setLoading(true);

      axios
        .post(API_URLS.createMultipleTasks, payload)
        .then(() => {
          onClose();
          showToast(
            "success",
            "Success",
            translateMessage({
              intl,
              id: "ws_creators_has_been_added_to_campaign",
              defaultMessage:
                "Creators has been added to the selected campaign.",
            }),
          );
        })
        .catch((err) => {
          if (
            err?.response.data.statusCode ===
            responseCodes["402_PAYMENT_REQUIRED"]
          ) {
            onClose();
            handle402Errors(dispatch, err?.response?.data?.errors);
          } else {
            showToast(
              "error",
              <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
              translateMessage({
                intl,
                id: "ws_creators_has_been_added_to_campaign-",
                defaultMessage:
                  "Error on adding Creators to the selected campaign.",
              }),
            );
          }

          setLoading(false);
        });
    }
  };

  const copyTaskToWsProjectWithValues = () => {
    if (selectedWsProject && selectedWsProject.value) {
      const tasksData = selectedCreators.map((creator: SelectedCreator) => {
        const isFacebookNumericName =
          creator.socialProfiles &&
          creator.socialProfiles[0].socialProvider ===
            SocialProvider.Facebook &&
          isFacebookNumericUsername(creator.name);

        if (creator.socialProfiles) {
          return {
            uuid: creator.uuid,
            name: creator.name,
            socialProfiles: creator.socialProfiles,
            rank: null,
            displayName: isFacebookNumericName ? creator?.displayName : null,
          };
        }

        return {
          uuid: creator.uuid,
          name: creator,
          rank: null,
          displayName: isFacebookNumericName ? creator?.displayName : null,
        };
      });

      const payload = {
        wsWorkspaceUuid: activeWorkspaceUuid,
        sourceWsProjectUuid: activeProjectUuid,
        targetWsProjectUuid: selectedWsProject.value,
        taskType: MetaFieldType.Creator,
        copyFieldValues,
        tasksData,
      };

      setLoading(true);

      axios
        .post(API_URLS.copySelectedTasksToProject, payload)
        .then(() => {
          onClose();
          showToast(
            "success",
            "Success",
            translateMessage({
              intl,
              id: "ws_creators_has_been_added_to_campaign",
              defaultMessage:
                "Creators has been added to the selected campaign.",
            }),
          );
        })
        .catch((err) => {
          if (
            err?.response.data.statusCode ==
            responseCodes["402_PAYMENT_REQUIRED"]
          ) {
            onClose();
            handle402Errors(dispatch, err?.response?.data?.errors);
          } else {
            showToast(
              "error",
              <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
              translateMessage({
                intl,
                id: "ws_error_adding_creating_to_the_selected_campaign",
                defaultMessage:
                  "Error on adding Creators to the selected campaign.",
              }),
            );
          }

          setLoading(false);
        });
    }
  };

  const addCreatorsToWorkspaceProject = () => {
    if (activeProjectUuid) {
      copyTaskToWsProjectWithValues();
    } else {
      copyTaskToWsProject();
    }
  };

  return (
    <Modal
      className="share-project-modal"
      overlayClassName="share-project-modal__overlay"
      onClose={onClose}
      displayCancelButton
      closeButtonText={translateMessage({
        intl,
        id: "ws_cancel",
        defaultMessage: "Cancel",
      })}
      onConfirmClick={addCreatorsToWorkspaceProject}
      confirmButtonLoading={loading}
      confirmButtonDisabled={!selectedWsProject}
      outsideClick={OutsideClickModes.Allow}
    >
      <ModalTitle>
        <IDHFormattedMessage
          id="ws_select_campaign"
          defaultMessage="Select campaign"
        />
        &nbsp;
        <RocketIcon />
      </ModalTitle>
      <ModalText>
        <IDHFormattedMessage
          id="ws_select_campaign_to_which_you_want_to_add_creators"
          defaultMessage="Select the campaign to which you want to add selected Creators."
        />
      </ModalText>
      <ModalRow>
        <ProjectsSearchSelector
          id="projectUuid"
          value={selectedWsProject}
          handleChange={setSelectedWsProject}
          afterMenuList={
            identity.permissions?.workspace.includes(
              WorkspacePermissionsEnum.CREATE_PROJECTS,
            ) ? (
              <div
                className="create-project-wrap"
                onClick={openCreateNewProjectModal}
              >
                <ImageOption icon={PlusIcon} className="create-project">
                  <IDHFormattedMessage
                    id="ws_create_new_campaign"
                    defaultMessage="Create new campaign"
                  />
                </ImageOption>
              </div>
            ) : null
          }
          placeholder={translateMessage({
            intl,
            id: "ws_select_campaign",
            defaultMessage: "Select Campaign",
          })}
          activeProjectUuid={activeProjectUuid}
        />
      </ModalRow>
      {activeProjectUuid && selectedWsProject?.value && (
        <ModalRow>
          <CustomCheckbox
            id="copy_task_values"
            name="copy_task_values"
            className="share-project-modal__copy-values-checkbox"
            checked={copyFieldValues}
            label={translateMessage({
              intl,
              id: "ws_copy_task_values",
              defaultMessage: "Copy field values",
            })}
            onChange={() => setCopyFieldValues(!copyFieldValues)}
          />
        </ModalRow>
      )}
    </Modal>
  );
};

export default injectIntl(AddCreatorsCDTWorkspaceProjectModal);
